import type { ComboboxData, ComboboxItem, TabsTabProps } from "@mantine/core";
import {
  DirektvermarktungContractRedispatchBilanzierungsmodell,
  DirektvermarktungContractRedispatchStatusDuldungsfall,
  DirektvermarktungContractRemunerationType,
  Veraeusserungsform,
  DirektvermarktungContractTrAbrechnungsmodell
} from "../../../../types/api.types";
import type { FormFieldData } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import type { ExpressionOperator } from "../../../BuildingBlocks/Forms/FormFields/ExpressionEditor/ExpressionEditor";
import type {
  DirektvermarktungContract,
  VermarktungAddons
} from "../../Direktvermarktung.types";

type TabProps = TabsTabProps & {
  label: string;
  hiddenInCreateMode?: boolean;
  showForAddons?: Array<VermarktungAddons>;
};

export const DV_CONTRACT_FORM_TABS = {
  GENERAL_INFORMATION: "allgemeine-informationen",
  REDISPATCH: "redispatch",
  ABREGELUNG: "abregelung",
  HISTORY: "history"
} as const;

export const TABS: Array<TabProps> = [
  {
    value: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION,
    label: "Allgemeine Informationen"
  },
  {
    value: DV_CONTRACT_FORM_TABS.REDISPATCH,
    label: "Redispatch",
    showForAddons: ["revenueMonitoring"]
  },
  {
    value: DV_CONTRACT_FORM_TABS.ABREGELUNG,
    label: "Abregelung",
    showForAddons: ["revenueMonitoring"]
  },
  {
    value: DV_CONTRACT_FORM_TABS.HISTORY,
    label: "Historie",
    hiddenInCreateMode: true
  }
];

export const DIREKTVERMARKTER_ANONYMOUS_ID = 507; // random ID from the database :(

export const DIREKTVERMARKTUNG_MARKETING_FORM_CHOICES = [
  {
    value: Veraeusserungsform.subsidized_direct_marketing,
    label: "Geförderte Direktvermarktung"
  },
  {
    value: Veraeusserungsform.other_direct_marketing,
    label: "Sonstige (Direkt-)Vermarktung"
  },
  {
    value: Veraeusserungsform.feste_einspeiseverguetung,
    label: "Feste Einspeisevergütung"
  }
] as const satisfies Array<ComboboxItem>;

const DIREKTVERMARKTUNG_CONTRACT_CHOICES = [
  {
    value:
      DirektvermarktungContractRemunerationType.monthly_market_value_contract,
    label: "Monatsmarktwert-Vertrag"
  },
  {
    value:
      DirektvermarktungContractRemunerationType.yearly_market_value_contract,
    label: "Jahresmarktwert-Vertrag"
  },
  {
    value: DirektvermarktungContractRemunerationType.spot_price_contract,
    label: "Spotpreis-Vertrag"
  },
  {
    value: DirektvermarktungContractRemunerationType.ppa_contract,
    label: "PPA (mit Fixpreiskomponente)"
  },
  {
    value: DirektvermarktungContractRemunerationType.other_verguetung,
    label: "Andere Vergütung"
  }
] as const satisfies Array<ComboboxItem>;

const FORMULA_OPERATORS = [
  { value: "+", description: "Addition" },
  { value: "-", description: "Subtraktion" },
  { value: "*", description: "Multiplikation" },
  { value: "/", description: "Division" },
  { value: "Gt( , )", description: "Größer als - Gt(Wert1, Wert2)" },
  { value: "Lt( , )", description: "Kleiner als - Lt(Wert1, Wert2)" },
  { value: "Ge( , )", description: "Größer gleich - Ge(Wert1, Wert2)" },
  { value: "Le( , )", description: "Kleiner gleich - Le(Wert1, Wert2)" },
  { value: "Eq( , )", description: "Gleich - Eq(Wert1, Wert2)" },
  { value: "Ne( , )", description: "Ungleich - Ne(Wert1, Wert2)" }
] as const satisfies Array<ExpressionOperator>;

const FORMULA_OTHER_ELEMENTS = [
  { value: "(", description: "Öffnende Klammer" },
  { value: ")", description: "Schließende Klammer" },
  {
    value: "Wenn( , , )",
    description: "Wenn-Dann-Sonst - Wenn(Bedingung, Dann, Sonst)"
  },
  { value: "Min( , )", description: "Minimum - Min(Wert1, Wert2)" },
  { value: "Max( , )", description: "Maximum - Max(Wert1, Wert2)" }
] as const satisfies Array<ExpressionOperator>;

const DIREKTVERMARKTUNG_STATUS_DULDUNGSFALL_CHOICES = [
  {
    value:
      DirektvermarktungContractRedispatchStatusDuldungsfall.AUFFORDERUNGSFALL,
    label: "Aufforderungsfall"
  },
  {
    value: DirektvermarktungContractRedispatchStatusDuldungsfall.DULDUNGSFALL,
    label: "Duldungsfall"
  }
] as const satisfies ComboboxData;

const DIREKTVERMARKTUNG_BILANZIERUNGSMODELL_CHOICES = [
  {
    value:
      DirektvermarktungContractRedispatchBilanzierungsmodell.PLANWERTMODELL,
    label: "Planwertmodell"
  },
  {
    value:
      DirektvermarktungContractRedispatchBilanzierungsmodell.PROGNOSEMODELL,
    label: "Prognosemodell"
  },
  {
    value:
      DirektvermarktungContractRedispatchBilanzierungsmodell.PROGNOSE_MIT_PLANUNGSDATENLIEFERUNG,
    label: "Prognose mit Planungsdatenlieferung"
  },
  {
    value:
      DirektvermarktungContractRedispatchBilanzierungsmodell.PROGNOSE_MIT_PROBEPLANUNGSDATENLIEFERUNG,
    label: "Prognose mit Probeplanungsdatenlieferung"
  }
] as const satisfies ComboboxData;

const DIREKTVERMARKTUNG_TR_ABRECHNUNGSMODELL_CHOICES = [
  {
    value: DirektvermarktungContractTrAbrechnungsmodell.PAUSCHAL,
    label: "Pauschal"
  },
  {
    value: DirektvermarktungContractTrAbrechnungsmodell.SPITZ,
    label: "Spitz"
  },
  {
    value: DirektvermarktungContractTrAbrechnungsmodell.SPITZ_LIGHT,
    label: "Spitz-Light"
  }
] as const satisfies ComboboxData;

export const DIREKTVERMARKTUNG_FORM_INPUT_DATA = {
  name: {
    name: "name",
    required: true,
    label: "Vertragsname",
    type: "text",
    group: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  },
  operator_id: {
    name: "operator_id",
    required: true,
    label: "Betreibergesellschaft",
    type: "select",
    group: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  },
  direktvermarkter_id: {
    name: "direktvermarkter_id",
    alias: "direktvermarkter",
    label: "Vermarkter",
    type: "select",
    clearable: true,
    helpText:
      "Bitte geben Sie den (Direkt)-Vermarkter Ihrer Anlage an. Sollte Ihr Vermarkter nicht in der Liste verfügbar sein, wenden Sie sich bitte an [support@node.energy](mailto:support@node.energy). Alternativ können Sie auch „anonymer Vermarkter“ auswählen, falls Sie keine spezifische Vermarktungsangabe machen möchten. Die Vermarkterangabe unterstützt die Vorauswahl von Entgelten und Vergütungen und erleichtert somit die weiteren Angaben.",
    group: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  },
  contract_remuneration_type: {
    name: "contract_remuneration_type",
    label: "Vergütung",
    type: "select",
    data: DIREKTVERMARKTUNG_CONTRACT_CHOICES,
    group: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  },
  first_date: {
    name: "first_date",
    label: "Startdatum",
    required: true,
    type: "text",
    group: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  },
  last_date: {
    name: "last_date",
    label: "Enddatum",
    required: true,
    type: "text",
    group: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  },
  formula_verguetung: {
    name: "formula_verguetung",
    label: "Vergütung in Euro",
    required: true,
    type: "expression",
    variables: [], // will be filled dynamically during runtime
    operators: FORMULA_OPERATORS,
    otherElements: FORMULA_OTHER_ELEMENTS,
    group: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  },
  formula_entgelt: {
    name: "formula_entgelt",
    label: "Vermarktungsentgelt in Euro",
    type: "expression",
    variables: [], // will be filled dynamically during runtime
    operators: FORMULA_OPERATORS,
    otherElements: FORMULA_OTHER_ELEMENTS,
    formulaTemplates: [], // will be filled dynamically during runtime
    group: DV_CONTRACT_FORM_TABS.GENERAL_INFORMATION
  },
  redispatch_status_duldungsfall: {
    name: "redispatch_status_duldungsfall",
    label: "Status Duldungsfall",
    type: "select",
    required: true,
    data: DIREKTVERMARKTUNG_STATUS_DULDUNGSFALL_CHOICES,
    group: DV_CONTRACT_FORM_TABS.REDISPATCH
  },
  redispatch_bilanzierungsmodell: {
    name: "redispatch_bilanzierungsmodell",
    label: "Bilanzierungsmodell",
    type: "select",
    required: true,
    data: DIREKTVERMARKTUNG_BILANZIERUNGSMODELL_CHOICES,
    group: DV_CONTRACT_FORM_TABS.REDISPATCH
  },
  redispatch_tr_abrechnungsmodell: {
    name: "redispatch_tr_abrechnungsmodell",
    label: "TR-Abrechnungsmodell",
    type: "select",
    required: true,
    data: DIREKTVERMARKTUNG_TR_ABRECHNUNGSMODELL_CHOICES,
    group: DV_CONTRACT_FORM_TABS.REDISPATCH
  },
  redispatch_has_dedicated_entgelt: {
    name: "redispatch_has_dedicated_entgelt",
    label:
      'Gibt es für die Redispatch-Mengen ein gesondertes oder abweichendes Entgelt (im Vergleich zu den Informationen unter "Allgemeine Informationen")?',
    required: true,
    type: "boolean",
    group: DV_CONTRACT_FORM_TABS.REDISPATCH
  },
  formula_redispatch_entgelt: {
    name: "formula_redispatch_entgelt",
    label: "Abweichendes Entgelt in Euro",
    type: "expression",
    required: true,
    variables: [], // will be filled dynamically during runtime
    operators: FORMULA_OPERATORS,
    otherElements: FORMULA_OTHER_ELEMENTS,
    formulaTemplates: [], // will be filled dynamically during runtime
    group: DV_CONTRACT_FORM_TABS.REDISPATCH
  },
  redispatch_has_dedicated_verguetung: {
    name: "redispatch_has_dedicated_verguetung",
    label:
      'Gibt es eine abweichende Vergütung für Redispatch-Mengen beim Vertragspartner/Vermarkter (im Vergleich zu den Informationen unter "Allgemeine Informationen")?',
    required: true,
    type: "boolean",
    group: DV_CONTRACT_FORM_TABS.REDISPATCH
  },
  formula_redispatch_verguetung: {
    name: "formula_redispatch_verguetung",
    label: "Abweichende Vergütung in Euro",
    type: "expression",
    required: true,
    variables: [], // will be filled dynamically during runtime
    operators: FORMULA_OPERATORS,
    otherElements: FORMULA_OTHER_ELEMENTS,
    formulaTemplates: [], // will be filled dynamically during runtime
    group: DV_CONTRACT_FORM_TABS.REDISPATCH
  },
  abregelung_tr_abrechnungsmodell: {
    name: "abregelung_tr_abrechnungsmodell",
    label: "Abrechnungsmodell für Mengen in marktbedingter Abschaltung",
    type: "select",
    required: true,
    data: DIREKTVERMARKTUNG_TR_ABRECHNUNGSMODELL_CHOICES,
    group: DV_CONTRACT_FORM_TABS.ABREGELUNG
  },
  abregelung_has_dedicated_entgelt: {
    name: "abregelung_has_dedicated_entgelt",
    label:
      'Gibt es für Abregelungen durch den Vermarkter ein gesondertes oder abweichendes Entgelt (im Vergleich zu den Informationen unter "Allgemeine Informationen")?',
    required: true,
    type: "boolean",
    group: DV_CONTRACT_FORM_TABS.ABREGELUNG
  },
  formula_abregelung_entgelt: {
    name: "formula_abregelung_entgelt",
    label: "Abweichendes Entgelt in Euro",
    type: "expression",
    required: true,
    variables: [], // will be filled dynamically during runtime
    operators: FORMULA_OPERATORS,
    otherElements: FORMULA_OTHER_ELEMENTS,
    formulaTemplates: [], // will be filled dynamically during runtime
    group: DV_CONTRACT_FORM_TABS.ABREGELUNG
  },
  abregelung_has_dedicated_verguetung: {
    name: "abregelung_has_dedicated_verguetung",
    label:
      'Gibt es eine abweichende Vergütung für die Mengen, die durch den Vermarkter abgeregelt werden (im Vergleich zu den Informationen unter "Allgemeine Informationen")?',
    required: true,
    type: "boolean",
    group: DV_CONTRACT_FORM_TABS.ABREGELUNG
  },
  formula_abregelung_verguetung: {
    name: "formula_abregelung_verguetung",
    label: "Abweichende Vergütung in Euro",
    type: "expression",
    required: true,
    variables: [], // will be filled dynamically during runtime
    operators: FORMULA_OPERATORS,
    otherElements: FORMULA_OTHER_ELEMENTS,
    formulaTemplates: [], // will be filled dynamically during runtime
    group: DV_CONTRACT_FORM_TABS.ABREGELUNG
  }
} as const satisfies FormFieldData<DirektvermarktungContract>;
